/** ==================== Auth Methods ==================== */
export enum AuthMethod {
  EMAIL = 'EMAIL',
  PASSWORD = 'PASSWORD',
  GOOGLE = 'GOOGLE',
  SAML = 'SAML',
  VERIFICATION_CODE = 'VERIFICATION_CODE'
}

/** ==================== Login Error Types ==================== */
export enum LoginErrorType {
  WRONG_CREDENTIALS = 'wrongCredentials',
  DO_NOT_HAVE_ACCOUNT_SSO = 'doNotHaveAccountSSO',
  MANDATORY_SSO = 'mandatorySSO',
  MANDATORY_SSO_SAML = 'mandatorySSOSAML',
  MANDATORY_PASSWORD = 'mandatoryPassword',
  DEACTIVATED_USER = 'deactivatedUser',
  DEACTIVATED_COMPANY = 'deactivatedCompany',
  EXPIRED_TOKEN = 'expiredToken',
  USED_TOKEN = 'usedToken',
  WRONG_VERIFICATION_CODE = 'wrongVerificationCode',
  EXPIRED_VERIFICATION_CODE = 'expiredVerificationCode',
  SENT_VERIFICATION_CODE = 'sentVerificationCode',
  USER_NOT_FOUND = 'userNotFound',
  SAML_AUTH_FAILED = 'samlAuthenticationFailed',
  MANDATORY_SKILL_OPS_REQUIREMENTS = 'mandatorySkillOpsRequirements',
  WRONG_RECAPTCHA = 'wrongReCaptcha',
  RECAPTCHA_NOT_READY = 'recaptchaNotReady',
  RECAPTCHA_TOKEN_FAILED = 'recaptchaTokenFailed',
  RECAPTCHA_TOKEN_EXPIRED = 'recaptchaTokenExpired',
  RECAPTCHA_TOKEN_DUPLICATE = 'recaptchaTokenDuplicate',
  RECAPTCHA_TOKEN_MISSING = 'recaptchaTokenMissing',
  RECAPTCHA_TOKEN_MALFORMED = 'recaptchaTokenMalformed',
  RECAPTCHA_ACTION_MISMATCH = 'recaptchaActionMismatch',
  RECAPTCHA_LOW_SCORE = 'recaptchaLowScore',
  RECAPTCHA_BROWSER_ERROR = 'recaptchaBrowserError',
  RECAPTCHA_TIMEOUT = 'recaptchaTimeout',
  NETWORK_ERROR = 'networkError',
  GENERIC_ERROR = 'genericError',
  // Reset Password
  RESET_EXPIRED_TOKEN = 'reset.password.expiredToken',
  RESET_INVALID_TOKEN = 'reset.password.invalidToken',
  STRENGTH_PASSWORD_NOT_ENOUGH = 'strengthPasswordNotEnough'
}

/** ==================== Login Request ==================== */
export interface EmailDomainCheckRequest {
  username: string
}

export interface LoginPasswordRequest {
  username: string
  password: string
  remember?: boolean | string
  recaptchaToken?: string
  uniqueId?: string
}

export interface LoginWithMFARequest {
  username: string
  mfaCode: string
  remember?: boolean | string
  uniqueId?: string
}

export interface LoginWithVerificationCodeRequest {
  username: string
  code: string
  remember?: boolean | string
  uniqueId?: string
}

export interface ResendCodeRequest {
  email: string
}

/** ==================== User Info ==================== */
export interface UserBasicInfo {
  id: string
  email: string
  name: string
  accountType: string
}

/** ==================== Login Response ==================== */
export interface EmailDomainCheckResponse {
  authMethod: AuthMethod | string
  redirectUrl?: string | null
  uniqueId?: string
}

export interface AuthenticateResponse {
  authMethod: AuthMethod | string
  redirectUrl?: string | null
  requiresVerificationCode?: boolean
  contactType?: string
  user?: UserBasicInfo
}

export interface VerifyCodeResponse {
  user: UserBasicInfo
}

export interface ResendCodeResponse {
  message: string
  contactType: string
}

// Legacy types for backward compatibility (v1)
export interface LoginPasswordResponse {
  success: boolean
  message?: string | null
  error?: LoginErrorType | string | null
  uniqueId?: string
}

export interface LoginWithMFAResponse {
  success: boolean
  message?: string | null
  error?: LoginErrorType | string | null
  uniqueId?: string
}

export interface LoginWithVerificationCodeResponse {
  success: boolean
  message?: string | null
  error?: LoginErrorType | string | null
  uniqueId?: string
}

export interface LoginWithGoogleResponse {
  success: boolean
  message?: string | null
}

/** ==================== Forgot Password Request ==================== */
export interface ForgotPasswordRequest {
  email: string
  recaptchaToken?: string
}

/** ==================== Forgot Password Response ==================== */
export interface ForgotPasswordResponse {
  success: boolean
  message?: string | null
  error?: LoginErrorType | string | null
}

/** ==================== Reset Password Request ==================== */
export interface ResetPasswordRequest {
  token: string
  password: string
  recaptchaToken?: string
}

/** ==================== Reset Password Response ==================== */
export interface ResetPasswordResponse {
  success: boolean
  message?: string | null
  error?: LoginErrorType | string | null
}

/** ==================== Resend Access Request ==================== */
export interface ResendAccessRequest {
  email: string
  recaptchaToken?: string
}

/** ==================== Resend Access Response ==================== */
export interface ResendAccessResponse {
  success: boolean
  message?: string | null
  error?: LoginErrorType | string | null
}
